// Texts of the channel audio screen: matrix, presets, loudness, A/V offset,
// meters and the output monitor (docs/design/i18n.md).

import { defineMessages } from '@/shared/i18n'

export const messages = defineMessages({
  ru: {
    notSavedYet: 'Обработка звука ещё не сохранена: фейдеры меняют черновик. Нажмите «Сохранить матрицу и громкость» — после этого громкость меняется вживую.',
    title: 'Звук канала',

    // model.ts
    chN: 'к{n}',
    layoutMono: 'моно',
    layoutStereo: 'стерео',
    layoutChannels: '{count} кан.',
    routeShort: 'Назначение …{id}',
    trackN: 'Дорожка {n}',
    busN: 'Выход {n}',
    loudnormLatency:
      'loudnorm добавляет ~3 с задержки всему выходу канала (изменение вступает в силу после перезапуска ветки звука make-before-break) — учтите при сведении со сдвигом A/V и с другими источниками той же линии.',
    syncExclusive: 'audio_delay_ms и video_delay_ms взаимоисключающие: заполните только одно поле',
    syncAudioRange: 'audio_delay_ms должен быть от 0 до {max}',
    syncVideoRange: 'video_delay_ms должен быть от 0 до {max}',

    // LoudnessPanel
    modeOff: 'Выключено',
    modeLoudnorm: 'loudnorm (EBU R128)',
    modeDynaudnorm: 'dynaudnorm (лёгкий)',
    loudnessTitle: 'Нормализация громкости',
    fieldMode: 'Режим',
    targetI: 'Целевая громкость (LUFS)',
    truePeak: 'Истинный пик (дБTP)',
    lra: 'Диапазон громкости, LU',

    // SyncPanel
    syncTitle: 'Сдвиг звука относительно видео',
    syncLead: 'Заполните только одно поле: смещается отстающая сторона.',
    audioDelay: 'Задержать звук, мс',
    videoDelay: 'Задержать видео, мс',
    previewRoute: 'Назначение для проверки',
    checkInPreview: 'Проверить в превью',
    syncNoRoutes:
      'Канал не подключён ни к одному назначению: проверить сдвиг в превью можно, когда появится хотя бы одно.',

    // AudioMeters
    inputCaption: 'Вход',
    noLayout: 'раскладка не задана',
    outputCaption: 'Выход',
    noBuses: 'выходов нет',
    meterIn: 'Вход, {name}',
    meterOut: 'Выход, {name}',
    silence: 'тишина',

    // AudioView
    loading: 'Загрузка…',
    screenTitle: 'Звук: {name}',
    audioEnabled: 'Включить обработку звука',
    backToChannel: 'К каналу',
    gainsError: 'Изменение фейдера не дошло до control: {error}',
    audioOff: 'Обработка звука выключена: в эфир идёт звук как пришёл.',
    audioOffWhat:
      'Включите её, чтобы менять громкость, выравнивать звук и собирать дорожки выхода из дорожек входа. Если обработка упадёт, в эфир пойдёт звук как есть.',
    audioTurnOn: 'Включить',
    saving: 'Сохраняем…',
    saveAudio: 'Сохранить матрицу и громкость',
    unsaved: 'есть несохранённые изменения',
    saveSync: 'Сохранить сдвиг',
    outputMonitoring: 'Контроль выхода',

    // PresetsPanel
    presetNameRequired: 'Укажите имя пресета.',
    presetsTitle: 'Пресеты матрицы',
    noPresets: 'Пресетов пока нет.',
    builtin: ' (встроенный)',
    apply: 'Применить',
    deleteLower: 'удалить',
    saveAsPreset: 'Сохранить как пресет',
    newPreset: 'Новый пресет',
    save: 'Сохранить',
    presetName: 'Имя пресета',
    deletePresetTitle: 'Удалить пресет «{name}»?',
    delete: 'Удалить',
    deletePresetNote: 'Каналы, уже использующие этот пресет, не изменятся: удаление затрагивает только библиотеку.',
    // Defect 10 (UIX 26.09): applying a preset used to overwrite the live
    // matrix on the first click - now confirmed, like save/delete already are.
    applyPresetTitle: 'Применить пресет «{name}»?',
    applyPresetNote: 'Матрица и раскладка этого канала будут заменены пресетом и сохранены сразу — включая канал, уже идущий в эфир.',

    // MatrixEditor
    matrixTitle: 'Матрица звука',
    suggestStereo: 'Предложить стереопару:',
    addInputTrack: '+ входная дорожка',
    addBus: '+ выход',
    noInputLayout: 'Раскладка входа не задана.',
    corner: 'Вход \\ выход',
    busNameLabel: 'Название выхода {n}',
    removeBus: 'Убрать выход «{name}»',
    connectCell: 'Соединить {input} с выходом «{bus}», {ch}',
    cellGain: 'Усиление, дБ: {input} → «{bus}», {ch}',
    masterDb: 'Мастер, дБ',
    db: '{value} дБ',
    dbAboveTravel: '{value} дБ: выше хода фейдера, задано числом',
    masterFader: 'Общая громкость выхода «{name}»',
    masterNumber: 'Общая громкость выхода «{name}», дБ',
    muteBus: 'Заглушить выход «{name}»',
    mute: 'заглушить',

    // AudioMonitor
    tapProcessed: 'После обработки',
    tapRoute: 'Что уходит в назначение',
    tapProcessedOff: 'обработка звука выключена или не сохранена',
    tapRouteOff: 'канал не подключён ни к одному назначению',
    monitorTitle: 'Что идёт на выход',
    closePreview: 'Закрыть превью',
    openPreview: 'Открыть превью',
    whatToWatch: 'Что смотреть',
    route: 'Назначение',
    routeTitle: 'Какое назначение смотреть',
    whichBus: 'Какой выход слушать',
    nothingToWatch: 'Смотреть нечего: {processed}; {route}.',
    previewStuck: 'Превью не восстанавливается уже 15 секунд.',
    reopen: 'Открыть заново',
    previewLabel: 'Превью: {tap}',
    previewOpening: 'Открываем превью…',
    previewClosed: 'Превью закрыто',
    until: 'до {time}',
    listen: 'Слушать',
    listenStop: 'Не слушать',
    listenTitle: 'Только звук выбранного выхода с минимальной задержкой (живой поток, отдельная сессия)',
    listenNoMse: 'Браузер не умеет MediaSource: слушайте звук превью',
    listenLabel: 'Звук: {tap}',
    listenOpening: 'Подключаем звук…',
  },
  en: {
    notSavedYet: 'Audio processing is not saved yet: the faders change the draft. Press “Save matrix and loudness” — after that the level changes live.',
    title: 'Channel audio',

    chN: 'ch{n}',
    layoutMono: 'mono',
    layoutStereo: 'stereo',
    layoutChannels: '{count} ch',
    routeShort: 'Destination …{id}',
    trackN: 'Track {n}',
    busN: 'Output {n}',
    loudnormLatency:
      'loudnorm adds ~3 s of delay to the whole channel output (the change takes effect after a make-before-break restart of the audio branch). Keep it in mind when mixing with an A/V offset and with other sources of the same line.',
    syncExclusive: 'audio_delay_ms and video_delay_ms are mutually exclusive: fill in only one field',
    syncAudioRange: 'audio_delay_ms must be from 0 to {max}',
    syncVideoRange: 'video_delay_ms must be from 0 to {max}',

    modeOff: 'Off',
    modeLoudnorm: 'loudnorm (EBU R128)',
    modeDynaudnorm: 'dynaudnorm (light)',
    loudnessTitle: 'Loudness normalisation',
    fieldMode: 'Mode',
    targetI: 'Target loudness (LUFS)',
    truePeak: 'True peak (dBTP)',
    lra: 'Loudness range, LU',

    syncTitle: 'Audio offset against video',
    syncLead: 'Fill in only one field: the side that lags is shifted.',
    audioDelay: 'Delay audio, ms',
    videoDelay: 'Delay video, ms',
    previewRoute: 'Destination to check',
    checkInPreview: 'Check in preview',
    syncNoRoutes:
      'The channel is not connected to any destination: the offset can be checked in preview once there is at least one.',

    inputCaption: 'Input',
    noLayout: 'no layout set',
    outputCaption: 'Output',
    noBuses: 'no outputs',
    meterIn: 'Input, {name}',
    meterOut: 'Output, {name}',
    silence: 'silence',

    loading: 'Loading…',
    screenTitle: 'Audio: {name}',
    audioEnabled: 'Turn audio processing on',
    backToChannel: 'Back to channel',
    gainsError: 'The fader change did not reach control: {error}',
    audioOff: 'Audio processing is off: the sound goes on air as received.',
    audioOffWhat:
      'Turn it on to change the level, normalise the loudness and build the output tracks from the input tracks. If the processing fails, the sound goes on air as is.',
    audioTurnOn: 'Turn on',
    saving: 'Saving…',
    saveAudio: 'Save matrix and loudness',
    unsaved: 'unsaved changes',
    saveSync: 'Save offset',
    outputMonitoring: 'Output monitoring',

    presetNameRequired: 'Enter a preset name.',
    presetsTitle: 'Matrix presets',
    noPresets: 'No presets yet.',
    builtin: ' (built-in)',
    apply: 'Apply',
    deleteLower: 'delete',
    saveAsPreset: 'Save as a preset',
    newPreset: 'New preset',
    save: 'Save',
    presetName: 'Preset name',
    deletePresetTitle: 'Delete preset “{name}”?',
    delete: 'Delete',
    deletePresetNote: 'Channels that already use this preset do not change: deleting affects only the library.',
    applyPresetTitle: 'Apply preset “{name}”?',
    applyPresetNote: 'This channel\'s matrix and layout will be replaced by the preset and saved at once - including a channel already on air.',

    matrixTitle: 'Audio matrix',
    suggestStereo: 'Suggest a stereo pair:',
    addInputTrack: '+ input track',
    addBus: '+ output',
    noInputLayout: 'No input layout set.',
    corner: 'Input \\ output',
    busNameLabel: 'Output {n} name',
    removeBus: 'Remove output “{name}”',
    connectCell: 'Route {input} to output “{bus}”, {ch}',
    cellGain: 'Gain, dB: {input} → “{bus}”, {ch}',
    masterDb: 'Master, dB',
    db: '{value} dB',
    dbAboveTravel: '{value} dB: above fader travel, set as a number',
    masterFader: 'Overall level of output “{name}”',
    masterNumber: 'Overall level of output “{name}”, dB',
    muteBus: 'Mute output “{name}”',
    mute: 'mute',

    tapProcessed: 'After processing',
    tapRoute: 'What goes to the destination',
    tapProcessedOff: 'audio processing is off or not saved',
    tapRouteOff: 'the channel is not connected to any destination',
    monitorTitle: 'What goes out',
    closePreview: 'Close preview',
    openPreview: 'Open preview',
    whatToWatch: 'What to watch',
    route: 'Destination',
    routeTitle: 'Which destination to watch',
    whichBus: 'Which output to listen to',
    nothingToWatch: 'Nothing to watch: {processed}; {route}.',
    previewStuck: 'The preview has not recovered for 15 seconds.',
    reopen: 'Reopen',
    previewLabel: 'Preview: {tap}',
    previewOpening: 'Opening preview…',
    previewClosed: 'Preview closed',
    until: 'until {time}',
    listen: 'Listen',
    listenStop: 'Stop listening',
    listenTitle: 'Only the sound of the selected output, with the lowest delay (live stream, a separate session)',
    listenNoMse: 'This browser has no MediaSource: listen to the preview sound',
    listenLabel: 'Sound: {tap}',
    listenOpening: 'Connecting the sound…',
  },
})
